import { motion, useAnimation, useMotionValue, useReducedMotion, useSpring } from "framer-motion";
import { useEffect, useRef } from "react";

const revealSelector = [
  ".section-heading",
  ".benefits-strip .container > div",
  ".process-grid article",
  ".savings-card",
  ".faq-item",
  ".final-cta__inner",
  ".product-detail__visual",
  ".product-detail__content",
  ".product-story > div",
  ".cart-line",
  ".order-summary",
  ".form-section",
  ".contact-details",
  ".contact-form",
  ".editorial-warning",
  ".editorial-sections article",
  ".editorial-cta",
  ".footer__trust > div",
].join(",");

function FloatingStamp({ position, index }) {
  const controls = useAnimation();
  const offset = useRef({ x: 0, y: 0 });

  function escapePointer(event) {
    if (event.pointerType === "touch") return;
    const rectangle = event.currentTarget.getBoundingClientRect();
    const margin = 24;
    const targetLeft = margin + Math.random() * Math.max(1, window.innerWidth - rectangle.width - margin * 2);
    const targetTop = margin + Math.random() * Math.max(1, window.innerHeight - rectangle.height - margin * 2);
    offset.current = {
      x: offset.current.x + targetLeft - rectangle.left,
      y: offset.current.y + targetTop - rectangle.top,
    };
    controls.start({
      x: offset.current.x,
      y: offset.current.y,
      rotateX: 360 + Math.random() * 80,
      rotateY: (index % 2 ? -1 : 1) * (180 + Math.random() * 120),
      rotateZ: -18 + Math.random() * 36,
      scale: [1, .72, 1.08, 1],
      transition: { type: "spring", stiffness: 125, damping: 16, mass: .82 },
    });
  }

  return <motion.span className={`floating-stamp floating-stamp--${position}`} animate={controls} onPointerEnter={escapePointer} whileHover={{ filter: "saturate(1.08) brightness(1.05)" }}>
    <span className="floating-stamp__card"><img src="/assets/products/timbre-lettre-verte.jpg" alt="" /><i /></span>
  </motion.span>;
}

export function SiteEffects({ routeKey }) {
  const reducedMotion = useReducedMotion();
  const pointerX = useMotionValue(-500);
  const pointerY = useMotionValue(-500);
  const x = useSpring(pointerX, { stiffness: 95, damping: 22, mass: .55 });
  const y = useSpring(pointerY, { stiffness: 95, damping: 22, mass: .55 });

  useEffect(() => {
    if (reducedMotion || !window.matchMedia("(pointer: fine)").matches) return undefined;
    const move = (event) => { pointerX.set(event.clientX); pointerY.set(event.clientY); };
    window.addEventListener("pointermove", move, { passive: true });
    return () => window.removeEventListener("pointermove", move);
  }, [pointerX, pointerY, reducedMotion]);

  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return undefined;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-revealed");
        observer.unobserve(entry.target);
      });
    }, { threshold: .12, rootMargin: "0px 0px -35px" });

    const register = (root) => {
      const nodes = [];
      if (root instanceof Element && root.matches(revealSelector)) nodes.push(root);
      if (root.querySelectorAll) nodes.push(...root.querySelectorAll(revealSelector));
      nodes.forEach((node) => {
        if (!node.classList.contains("fx-reveal")) {
          const siblings = node.parentElement ? [...node.parentElement.children] : [];
          node.style.setProperty("--reveal-delay", `${Math.min(240, (siblings.indexOf(node) % 4) * 65)}ms`);
          node.classList.add("fx-reveal");
        }
        if (reducedMotion) node.classList.add("is-revealed");
        else if (!node.classList.contains("is-revealed")) observer.observe(node);
      });
    };
    register(main);
    const mutation = new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) register(node);
    })));
    mutation.observe(main, { childList: true, subtree: true });
    return () => { mutation.disconnect(); observer.disconnect(); };
  }, [routeKey, reducedMotion]);

  return <div className="site-effects" aria-hidden="true">
    {!reducedMotion && <motion.span className="site-effects__pointer" style={{ x, y }} />}
    <span className="site-effects__orb site-effects__orb--one" />
    <span className="site-effects__orb site-effects__orb--two" />
    <div className="floating-stamps">
      {["one", "two", "three", "four", "five"].map((position, index) => <FloatingStamp position={position} index={index} key={position} />)}
    </div>
  </div>;
}
